/**
 * Merlin edit-proposal card — the CSR confirm surface for change-order emails.
 *
 * Rendered on the order detail when the email pipeline persisted a Merlin
 * proposal against this OPEN Sage order (order_edit_proposals, FSM
 * 'proposed'). Shows the plain-language summary + the old→new diff the
 * backend computed from DB truth, and three decisions:
 *
 * - "Confirm & send to Sage" → POST /portal/orders/{id}/edit-proposals/{pid}/
 *   confirm — the audited apply_order_edit path (merlin_proposed source,
 *   one-shot proposal_key). The agent itself can never apply anything.
 * - "Adjust values" → the proposed changes become editable (review b6db3995:
 *   quantities/prices tweakable, rows untickable); Confirm then sends the
 *   adjusted set, which the backend re-validates against DB truth before
 *   applying and records on the proposal's confirmed_payload.
 * - "Dismiss" → terminal decline, nothing applied. The Dismiss button first
 *   opens an OPTIONAL one-line reason box (owner rulings, reviews 07e9f07a +
 *   7a3fa712: track the reason in the database when given, never force it —
 *   a filled reason seeds a future Merlin learning loop), then sends.
 */
import { useState } from 'react';

import {
  portalOrderService,
  type OrderEditProposalRead,
  type SageFieldChangePayload,
  type UnproposedDeltaPayload,
} from '../../services/portalOrders';
import {
  buildEditsPayload,
  confirmNoPriceRows,
  initEditRows,
  unpricedAddCodes,
  type EditableProposalRow,
} from './merlinEditProposalEdits';
import ExtractionConcernsBanner from './ExtractionConcernsBanner';
import styles from './MerlinEditProposalCard.module.css';

type Phase = 'idle' | 'confirming' | 'dismissing' | 'done';

/** Longest dismiss reason the backend stores (OrderEditProposalDismissBody). */
export const DISMISS_REASON_MAX_CHARS = 500;

/** Above this many change rows, show the explicit count + the list scrolls. */
const MANY_CHANGES_THRESHOLD = 6;
/** Summaries longer than this collapse behind Show more. */
const SUMMARY_CLAMP_CHARS = 220;

function diffValue(v: unknown): string {
  if (v == null || v === '') return '(blank)';
  return String(v);
}

const DELTA_ASPECT_LABEL: Record<string, string> = {
  quantity: 'qty',
  unit_price: 'price',
  description: 'description',
  product: 'product',
  other: 'line',
};

/**
 * Unmarked differences Merlin found on the revised PO but did NOT propose
 * (kanban ced71e0f). Read-only: nothing here is sent to Sage and nothing is
 * adjustable — the list exists so a re-priced or renamed line the customer
 * did not mark still gets a human look instead of "all other lines
 * unchanged". Rendered in both the proposal and Adjust-values views.
 */
function UnproposedDeltas({
  deltas,
  productNameFor,
}: {
  deltas: UnproposedDeltaPayload[];
  productNameFor: (code: string) => string | undefined;
}) {
  if (deltas.length === 0) return null;
  return (
    <div className={styles.deltaSection} data-testid="proposal-unproposed-deltas">
      <div className={styles.deltaTitle}>
        Also different on the revised PO — not proposed ({deltas.length})
      </div>
      <div className={styles.deltaHint}>
        Merlin saw these differences but left the order as is. Nothing below is
        sent to Sage — check each one; edit the order by hand if the customer
        meant it.
      </div>
      {deltas.map((d, i) => {
        const code = d.item_code || 'line';
        const aspect = DELTA_ASPECT_LABEL[d.aspect] ?? 'line';
        const productName = d.item_code ? productNameFor(d.item_code) : undefined;
        return (
          <div
            key={`${d.line_item_id ?? 'none'}-${d.aspect}-${i}`}
            className={styles.deltaRow}
            data-testid="proposal-unproposed-delta"
          >
            <span className={styles.editLabel}>
              <span className={styles.diffField}>
                {code} {aspect}
              </span>
              {productName && <span className={styles.productName}>{productName}</span>}
            </span>
            <span className={styles.diffValues}>
              {d.order_value ? (
                <>
                  <span className={styles.deltaValueLabel}>order</span>{' '}
                  <span className={styles.diffOld}>{diffValue(d.order_value)}</span>
                  <span className={styles.diffArrow} aria-hidden="true">·</span>
                </>
              ) : null}
              <span className={styles.deltaValueLabel}>PO</span>{' '}
              <span className={styles.deltaPoValue}>{diffValue(d.po_value)}</span>
            </span>
            {d.reason && <span className={styles.deltaReason}>{d.reason}</span>}
          </div>
        );
      })}
    </div>
  );
}

function DiffRow({
  change,
  productName,
  priceRequired = false,
}: {
  change: SageFieldChangePayload;
  productName?: string;
  /** The add has no price the server could settle — a CSR must key one. */
  priceRequired?: boolean;
}) {
  const field = (
    <span className={styles.editLabel}>
      <span className={styles.diffField}>{change.field}</span>
      {productName && <span className={styles.productName}>{productName}</span>}
    </span>
  );
  if (change.kind === 'line_add') {
    return (
      <div className={styles.diffRow}>
        {field}
        <span className={styles.diffValues}>
          <span className={styles.diffAdd}>+ add {diffValue(change.new)}</span>
          {priceRequired && (
            <span
              className={styles.priceRequired}
              data-testid="proposal-price-required"
              title="No contract, quote, or catalog price was found for this customer. Enter one under Adjust values, or confirm sending without a price — Sage will store the line at $0.00."
            >
              no price
            </span>
          )}
        </span>
      </div>
    );
  }
  if (change.kind === 'line_remove') {
    return (
      <div className={styles.diffRow}>
        {field}
        <span className={styles.diffValues}>
          <span className={styles.diffRemove}>− remove {diffValue(change.old)}</span>
        </span>
      </div>
    );
  }
  if (change.kind === 'charge_note') {
    // An invoice-time charge (/SURCHG) the customer/rep asked for: Sage bills
    // it from the account's surcharge status, so there is nothing to send to
    // the sales order. Display-only — Confirm applies nothing for this row.
    return (
      <div className={styles.diffRow} data-testid="proposal-charge-note">
        {field}
        <span className={styles.diffValues}>
          <span className={styles.chargeNote}>{diffValue(change.new)}</span>
        </span>
      </div>
    );
  }
  if (change.kind === 'fulfillment_note') {
    // A shipping instruction with no Sage field ("ship complete — no partial
    // shipments"). Shown so the CSR acknowledges it instead of it vanishing
    // into the summary; Confirm applies nothing for this row.
    return (
      <div className={styles.diffRow} data-testid="proposal-fulfillment-note">
        {field}
        <span className={styles.diffValues}>
          <span className={styles.chargeNote}>{diffValue(change.new)}</span>
        </span>
      </div>
    );
  }
  return (
    <div className={styles.diffRow}>
      {field}
      <span className={styles.diffValues}>
        <span className={styles.diffOld}>{diffValue(change.old)}</span>
        <span className={styles.diffArrow} aria-hidden="true">→</span>
        <span className={styles.diffNew}>{diffValue(change.new)}</span>
      </span>
    </div>
  );
}

function EditRow({
  row,
  onChange,
}: {
  row: EditableProposalRow;
  onChange: (next: EditableProposalRow) => void;
}) {
  const numericInputs = row.kind === 'line_update' || row.kind === 'line_add';
  return (
    <div className={styles.diffRow} data-testid="proposal-edit-row">
      <label className={styles.editInclude}>
        <input
          type="checkbox"
          checked={row.included}
          onChange={(e) => onChange({ ...row, included: e.target.checked })}
        />
        <span className={styles.editLabel}>
          <span className={styles.diffField}>{row.label}</span>
          {row.description && (
            <span className={styles.productName}>{row.description}</span>
          )}
        </span>
        {row.kind === 'line_add' && <span className={styles.diffAdd}>add</span>}
        {row.kind === 'line_add' && (row.unitPrice ?? '') === '' && (
          <span
            className={styles.priceRequired}
            data-testid="proposal-price-required"
            title="No price keyed and none could be resolved — enter one, or confirm sending without a price; Sage will store the line at $0.00."
          >
            no price
          </span>
        )}
        {row.kind === 'line_remove' && (
          <span className={styles.diffRemove}>remove</span>
        )}
      </label>
      {numericInputs && (
        <span className={styles.editInputs}>
          <label>
            qty
            <input
              type="number"
              min="0"
              step="1"
              className={styles.editInput}
              value={row.quantity ?? ''}
              disabled={!row.included}
              onChange={(e) => onChange({ ...row, quantity: e.target.value })}
              aria-label={`${row.label} quantity`}
            />
          </label>
          <label>
            price
            <input
              type="number"
              min="0"
              step="0.01"
              className={styles.editInput}
              value={row.unitPrice ?? ''}
              disabled={!row.included}
              onChange={(e) => onChange({ ...row, unitPrice: e.target.value })}
              aria-label={`${row.label} unit price`}
            />
          </label>
        </span>
      )}
      {row.kind === 'header' && (
        <span className={styles.diffValues}>
          <span className={styles.diffNew}>{diffValue(row.headerValue)}</span>
        </span>
      )}
    </div>
  );
}

export default function MerlinEditProposalCard({
  orderId,
  proposal,
  onResolved,
  lineItems,
  targetIsDraft = false,
}: {
  orderId: string;
  proposal: OrderEditProposalRead;
  /** Called after a successful confirm/dismiss so the parent refetches. */
  onResolved: () => void;
  /** The order's current lines — labels the editable update/remove rows
   *  with their code AND product name (review 1058222a). */
  lineItems?: Array<{
    id: string;
    item_code: string;
    item_description?: string | null;
  }>;
  /** The target is still a portal DRAFT (kanban 3db03d35): confirming
   *  applies the change to the draft here — nothing goes to Sage until the
   *  draft itself is submitted — and the draft is held from submission
   *  while the proposal is un-answered. */
  targetIsDraft?: boolean;
}) {
  // Adds the server could not price (kanban dc8caa8d): Merlin's proposal is
  // still reviewable, but sending the line unpriced makes Sage store it at
  // $0.00 — the server refuses that unless the CSR confirms it. So the card
  // OPENS in Adjust values with the price input in front of the CSR (review
  // y4yvpz: "I don't see a place for them to put in the price"), and Confirm
  // with the box still empty asks "you didn't add a price — send it anyway?"
  // ("there are times when the price isn't required").
  const unpricedAdds = unpricedAddCodes(proposal.payload);
  const buildRows = (): EditableProposalRow[] => {
    const infoById = Object.fromEntries(
      (lineItems ?? []).map((li) => [
        li.id,
        { item_code: li.item_code, item_description: li.item_description },
      ]),
    );
    return initEditRows(
      {
        header: proposal.payload.header as
          | Record<string, string | null>
          | null
          | undefined,
        line_updates: proposal.payload.line_updates,
        line_adds: proposal.payload.line_adds,
        line_removes: proposal.payload.line_removes,
      },
      infoById,
    );
  };
  const priceNeededMessage =
    unpricedAdds.length > 0
      ? `You didn't add a price for ${unpricedAdds.join(', ')}, and no ` +
        'contract, quote, or catalog price was found. Enter one, or confirm ' +
        'sending without a price — Sage will store the line at $0.00.'
      : null;

  const [phase, setPhase] = useState<Phase>('idle');
  // Dismiss asks for a reason first; null = the box is closed.
  const [dismissReason, setDismissReason] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState(unpricedAdds.length > 0);
  const [summaryExpanded, setSummaryExpanded] = useState(false);
  const [rows, setRows] = useState<EditableProposalRow[] | null>(() =>
    unpricedAdds.length > 0 ? buildRows() : null,
  );
  // Item codes awaiting the CSR's "send without a price" answer.
  const [noPricePrompt, setNoPricePrompt] = useState<string[] | null>(null);
  const changes = proposal.payload.changes ?? [];
  // Differences Merlin declined to propose ride beside `changes`, never in
  // it: they are not edits, so they never count toward the change total and
  // never enter the Adjust-values rows.
  const unproposedDeltas = proposal.payload.unproposed_deltas ?? [];
  // Merlin's questions for the CSR. The edit prompt always asked for them,
  // but until kanban ced71e0f the validator dropped the key (AU02071: "brown
  // line price differs on PO" never reached the card).
  const openQuestions = proposal.payload.open_questions ?? [];
  // Nothing in the payload is a Sage edit (every row is a display-only
  // charge_note / fulfillment_note): the card is an acknowledgement, not an
  // edit — no values to adjust, and Confirm records the CSR's OK without a
  // Sage write.
  const p = proposal.payload;
  const hasSageEdits =
    Object.keys(p.header ?? {}).length > 0 ||
    (p.line_updates?.length ?? 0) > 0 ||
    (p.line_adds?.length ?? 0) > 0 ||
    (p.line_removes?.length ?? 0) > 0;
  const displayOnlyKinds = new Set(['charge_note', 'fulfillment_note']);
  const acknowledgeOnly = !hasSageEdits && changes.length > 0 &&
    changes.every((c) => displayOnlyKinds.has(c.kind ?? ''));

  // Product name per item code, for the read-only diff rows: the order's
  // current lines plus any proposed adds (whose names ride in the payload).
  const nameByCode: Record<string, string> = {};
  for (const li of lineItems ?? []) {
    if (li.item_description) nameByCode[li.item_code] = li.item_description;
  }
  for (const add of proposal.payload.line_adds ?? []) {
    if (add.item_description) nameByCode[add.item_code] = add.item_description;
  }
  const productNameFor = (field: string): string | undefined => {
    // Diff fields are "<code>", "<code> qty", or "<code> price".
    const code = field.replace(/ (qty|price)$/, '');
    return nameByCode[code];
  };

  const startEditing = () => {
    if (rows === null) setRows(buildRows());
    setError(null);
    setEditing(true);
  };

  const act = async (
    kind: 'confirm' | 'dismiss',
    opts: { sendWithoutPrice?: boolean } = {},
  ) => {
    let edits;
    if (kind === 'confirm' && !editing && priceNeededMessage !== null) {
      // The CSR went "Back to proposal" and tried verbatim — same rule.
      startEditing();
      setError(priceNeededMessage);
      return;
    }
    if (kind === 'confirm' && editing && rows !== null) {
      const effectiveRows = opts.sendWithoutPrice ? confirmNoPriceRows(rows) : rows;
      if (opts.sendWithoutPrice) setRows(effectiveRows);
      const built = buildEditsPayload(effectiveRows);
      if (built.error !== null) {
        setError(built.error);
        return;
      }
      if (built.confirmNoPrice !== null) {
        setError(null);
        setNoPricePrompt(built.confirmNoPrice);
        return;
      }
      edits = built.edits;
    }
    setNoPricePrompt(null);
    setPhase(kind === 'confirm' ? 'confirming' : 'dismissing');
    setError(null);
    try {
      if (kind === 'confirm') {
        const res = await portalOrderService.confirmEditProposal(
          orderId,
          proposal.id,
          edits ?? undefined,
        );
        if (!res.success) {
          // The proposal is CONFIRMED (local edits applied) but the SAGE
          // push failed — its red flag / re-push is the recovery path.
          // Keep the card on screen with the error instead of refetching:
          // a refetch remounts the tab and would wipe this message.
          setError(
            (res.error_message ||
              'The Sage push failed — the edit is applied to our record.') +
              ' The failed push is flagged for follow-up; no need to confirm again.',
          );
          setPhase('done');
          return;
        }
      } else {
        await portalOrderService.dismissEditProposal(
          orderId,
          proposal.id,
          (dismissReason ?? '').trim(),
        );
      }
      setPhase('done');
      onResolved();
    } catch (e) {
      const err = e as {
        response?: { status?: number; data?: { detail?: string } };
        message?: string;
      };
      setError(err?.response?.data?.detail || err?.message || 'Request failed');
      // 409 = the proposal can no longer be confirmed as-is: it is no longer
      // 'proposed', or its change was already keyed by hand after Merlin
      // proposed it (AU02146 — a confirm would double the line). Freeze the
      // card on the explanation instead of inviting a second click.
      setPhase(err?.response?.status === 409 ? 'done' : 'idle');
    }
  };

  const busy = phase === 'confirming' || phase === 'dismissing';
  return (
    <div className={styles.card} data-testid="merlin-edit-proposal-card">
      <div className={styles.head}>
        <span className={styles.headIcon} aria-hidden="true">✏️</span>
        <div className={styles.headText}>
          <span className={styles.title}>
            Merlin proposes changes from a customer email
          </span>
          <span className={styles.subtitle} data-testid="proposal-subtitle">
            {targetIsDraft
              ? 'This order is still a draft. Confirming updates the draft here — ' +
                'nothing goes to Sage until you submit the order, and the draft ' +
                'cannot be submitted until this proposal is confirmed or dismissed.'
              : 'Nothing changes in Sage until you confirm — this goes through the ' +
                'same audited path as a manual edit.'}
          </span>
        </div>
      </div>
      {proposal.summary && (
        <div className={styles.summaryNote}>
          {/* The toggle must live OUTSIDE the clamped text node — an inline
              child gets clipped by -webkit-line-clamp along with the text. */}
          <div
            className={
              summaryExpanded || proposal.summary.length <= SUMMARY_CLAMP_CHARS
                ? undefined
                : styles.summaryClamped
            }
            data-testid="proposal-summary"
          >
            {proposal.summary}
          </div>
          {proposal.summary.length > SUMMARY_CLAMP_CHARS && (
            <button
              type="button"
              className={styles.summaryToggle}
              data-testid="proposal-summary-toggle"
              onClick={() => setSummaryExpanded((v) => !v)}
            >
              {summaryExpanded ? 'Show less' : 'Show more'}
            </button>
          )}
        </div>
      )}
      {openQuestions.length > 0 && (
        <div data-testid="proposal-open-questions">
          <ExtractionConcernsBanner
            concerns={openQuestions}
            label={`Merlin's question${openQuestions.length > 1 ? 's' : ''} for you (${openQuestions.length})`}
          />
        </div>
      )}
      {/* Large revisions: the row list scrolls inside the card (max ~40vh)
          so Confirm/Dismiss stay reachable; the count makes it explicit that
          there are more rows than fit (review 4f97674d — "what about the
          case where it's too large?"). */}
      {(editing && rows !== null ? rows.length : changes.length) >
        MANY_CHANGES_THRESHOLD && (
        <div className={styles.changeCount} data-testid="proposal-change-count">
          {editing && rows !== null ? rows.length : changes.length} proposed
          changes — scroll the list to review every one before confirming.
        </div>
      )}
      <div className={styles.lines}>
        {editing && rows !== null
          ? rows.map((row, i) => (
              <EditRow
                key={row.key}
                row={row}
                onChange={(next) =>
                  setRows((prev) =>
                    prev === null
                      ? prev
                      : prev.map((r, j) => (j === i ? next : r)),
                  )
                }
              />
            ))
          : changes.map((c, i) => (
              <DiffRow
                key={`${c.field}-${i}`}
                change={c}
                productName={productNameFor(c.field)}
                priceRequired={
                  c.kind === 'line_add' && unpricedAdds.includes(c.field)
                }
              />
            ))}
      </div>
      <UnproposedDeltas
        deltas={unproposedDeltas}
        productNameFor={(code) => nameByCode[code]}
      />
      {editing && priceNeededMessage !== null && noPricePrompt === null && (
        <div className={styles.priceNeeded} data-testid="proposal-price-needed">
          {priceNeededMessage}
        </div>
      )}
      {noPricePrompt !== null && (
        <div className={styles.priceNeeded} data-testid="proposal-no-price-prompt">
          You didn't add a price for {noPricePrompt.join(', ')}. Send it without
          a price? Sage will store the line at $0.00.
          <span className={styles.promptActions}>
            <button
              type="button"
              className={styles.promptBtn}
              disabled={busy}
              onClick={() => void act('confirm', { sendWithoutPrice: true })}
            >
              Yes, send without a price
            </button>
            <button
              type="button"
              className={styles.promptBtnQuiet}
              onClick={() => setNoPricePrompt(null)}
            >
              Go back
            </button>
          </span>
        </div>
      )}
      {editing && (
        <div className={styles.editHint}>
          Adjusted values are re-checked against the order before anything is
          applied. Untick a row to leave that change out.
        </div>
      )}
      {proposal.source_request_text && (
        <details className={styles.sourceDetails}>
          <summary>Customer's request</summary>
          <pre className={styles.sourceText}>{proposal.source_request_text}</pre>
        </details>
      )}
      {error && <div className={styles.errorNote}>{error}</div>}
      {dismissReason !== null && (
        <div className={styles.dismissBox} data-testid="proposal-dismiss-box">
          <label className={styles.dismissLabel} htmlFor={`dismiss-reason-${proposal.id}`}>
            Why dismiss this change? <span className={styles.dismissOptional}>Optional — recorded on the proposal and helps Merlin learn.</span>
          </label>
          <input
            id={`dismiss-reason-${proposal.id}`}
            className={styles.dismissInput}
            type="text"
            value={dismissReason}
            maxLength={DISMISS_REASON_MAX_CHARS}
            placeholder="e.g. already keyed in Sage / customer cancelled / wrong order"
            onChange={(e) => setDismissReason(e.target.value)}
            disabled={busy || phase === 'done'}
            autoFocus
          />
          <button
            type="button"
            className={styles.dismissConfirmBtn}
            onClick={() => void act('dismiss')}
            disabled={busy || phase === 'done'}
            data-testid="proposal-dismiss-confirm"
          >
            {phase === 'dismissing'
              ? 'Dismissing…'
              : dismissReason.trim()
                ? 'Dismiss with this reason'
                : 'Dismiss without a reason'}
          </button>
        </div>
      )}
      <div className={styles.actions}>
        {!acknowledgeOnly && (
          <button
            type="button"
            className={styles.dismissBtn}
            onClick={() => {
              if (editing) {
                setEditing(false);
                setRows(null);
                setError(null);
              } else {
                startEditing();
              }
            }}
            disabled={busy || phase === 'done'}
            data-testid="proposal-adjust-toggle"
          >
            {editing ? 'Back to proposal' : 'Adjust values'}
          </button>
        )}
        <button
          type="button"
          className={styles.dismissBtn}
          onClick={() => {
            if (dismissReason === null) {
              setDismissReason('');
              setError(null);
            } else {
              setDismissReason(null);
            }
          }}
          disabled={busy || phase === 'done'}
          data-testid="proposal-dismiss-toggle"
        >
          {dismissReason === null ? 'Dismiss' : 'Keep proposal'}
        </button>
        <button
          type="button"
          className={styles.confirmBtn}
          onClick={() => void act('confirm')}
          disabled={busy || phase === 'done'}
          data-testid="proposal-confirm"
        >
          {phase === 'confirming'
            ? acknowledgeOnly
              ? 'Recording…'
              : targetIsDraft
                ? 'Applying…'
                : 'Sending…'
            : acknowledgeOnly
              ? targetIsDraft
                ? 'Acknowledge — nothing to change on the draft'
                : 'Acknowledge — nothing to send to Sage'
              : targetIsDraft
                ? editing
                  ? 'Confirm adjusted & apply to draft'
                  : 'Confirm & apply to draft'
                : editing
                  ? 'Confirm adjusted & send to Sage'
                  : 'Confirm & send to Sage'}
        </button>
      </div>
    </div>
  );
}
